import { useCallback, useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { format, parseISO } from 'date-fns';
import { FIXTURE_MATCH_TIME } from '../../constants';
import { Club, Match, MatchStatus } from '@repo/types';
import { ClubNameType } from '../../contracts';
import { isFutureMatch, parseMatchStatus } from '../../../utils/parseMatchStatus';
import { ImageVariant, Image } from '../../../common/components/Image';
import { Link } from 'react-router';
import { isMobileClient } from '@ui/utils/isMobileClient';
import { LinkSize, LinkVariant } from '@ui/common/components/Link';
import { Link as LinkButton } from '@ui/common/components/Link';

export type FixtureProps = {
  fixture: Match;
  showAttendance?: boolean;
  className?: string;
  nameType?: ClubNameType;
  style?: React.CSSProperties;
  isFixtureExpandable?: boolean;
  isExpanded?: boolean;
};

type PlayerGoals = {
  player: string;
  goals: number[];
};

type PlayerRedCard = {
  player: string;
  redCard: number;
};

const sortGoals = (a: PlayerGoals, b: PlayerGoals) => {
  if (a.goals.length !== b.goals.length) {
    return a.goals.length > b.goals.length ? -1 : 1;
  }

  if (!a.goals[0]) {
    return -1;
  }

  if (!b.goals[0]) {
    return 1;
  }

  return a.goals[0] > b.goals[0] ? 1 : -1;
};

export const Fixture = ({
  fixture,
  showAttendance = false,
  nameType,
  className,
  isFixtureExpandable = false,
  isExpanded: isExpandedExternal,
}: FixtureProps) => {
  const isMobile = isMobileClient();
  const [isExpanded, setIsExpanded] = useState(isExpandedExternal);

  // Sync local state when external prop changes
  useEffect(() => {
    if (isExpandedExternal !== undefined) {
      setIsExpanded(isExpandedExternal);
    }
  }, [isExpandedExternal]);

  const formattedTime = useMemo(() => {
    if (fixture.status === MatchStatus.NotKickedOff) {
      if (fixture.time) {
        let time = fixture.time;
        // eslint-disable-next-line prefer-const
        let [hour, minutes] = fixture.time.split(':');
        if (!hour || !minutes) {
          console.warn(`Invalid time "${fixture.time}" returned for fixture id: ${fixture.id}`);
          return 'N/A';
        }

        // If the hour is returned as a single digit, e.g. 9, we convert it to ISO by prefixing with zero: 09
        if (hour.length === 1) {
          time = `0${hour}:${minutes}`;
        }

        return format(parseISO(`2024-01-01T${time}`), FIXTURE_MATCH_TIME);
      }
      return 'N/A';
    }

    return '';
  }, [fixture]);

  const getClubName = (club: Club) => {
    switch (nameType) {
      case ClubNameType.ThreeLetterName:
        return club.threeLetterName ?? club.shortName ?? club.fullName;
      case ClubNameType.ShortName:
        return club.shortName ?? club.fullName;
      case ClubNameType.FullName:
        return club.fullName;
      case ClubNameType.NickName:
        return club.nickName ?? club.shortName ?? club.fullName;
      default:
        return club.shortName;
    }
  };

  const toggleFixtureDetails = useCallback(() => {
    setIsExpanded(!isExpanded);
  }, [isExpanded]);

  const { isLiveMatch } = parseMatchStatus(fixture.status);

  const hasScores = ![MatchStatus.NotKickedOff, MatchStatus.Postponed, MatchStatus.Abandoned].includes(fixture.status);

  const playerStats = useMemo(() => {
    const homeTeamGoals: PlayerGoals[] = [];
    const homeTeamRedCards: PlayerRedCard[] = [];
    const awayTeamGoals: PlayerGoals[] = [];
    const awayTeamRedCards: PlayerRedCard[] = [];

    const homeTeamLineups = [...(fixture.lineups?.home ?? []), ...(fixture.subs?.home ?? [])];
    const awayTeamLineups = [...(fixture.lineups?.away ?? []), ...(fixture.subs?.away ?? [])];

    homeTeamLineups?.forEach((lineup) => {
      if (lineup.goals?.length) {
        homeTeamGoals.push({
          player:
            lineup.player.firstName && lineup.player.lastName
              ? `${lineup.player.firstName} ${lineup.player.lastName}`
              : lineup.player.firstName,
          goals: lineup.goals.map((goal) => goal.minute),
        });
      }
    });

    homeTeamLineups.forEach((lineup) => {
      if (lineup.redCard) {
        homeTeamRedCards.push({
          player:
            lineup.player.firstName && lineup.player.lastName
              ? `${lineup.player.firstName} ${lineup.player.lastName}`
              : lineup.player.firstName,
          redCard: lineup.redCard,
        });
      }
    });

    awayTeamLineups?.forEach((lineup) => {
      if (lineup.goals?.length) {
        awayTeamGoals.push({
          player:
            lineup.player.firstName && lineup.player.lastName
              ? `${lineup.player.firstName} ${lineup.player.lastName}`
              : lineup.player.firstName,
          goals: lineup.goals.map((goal) => goal.minute),
        });
      }
    });

    awayTeamLineups.forEach((lineup) => {
      if (lineup.redCard) {
        awayTeamRedCards.push({
          player:
            lineup.player.firstName && lineup.player.lastName
              ? `${lineup.player.firstName} ${lineup.player.lastName}`
              : lineup.player.firstName,
          redCard: lineup.redCard,
        });
      }
    });

    return {
      homeTeamGoals: homeTeamGoals.sort(sortGoals),
      homeTeamRedCards,
      awayTeamGoals: awayTeamGoals.sort(sortGoals),
      awayTeamRedCards,
    };
  }, [fixture]);

  const isExpandable = isFixtureExpandable && !isFutureMatch(fixture.status);

  const matchCurrentMinute = useMemo(() => {
    if (isLiveMatch && fixture.currentMinute) {
      const [minute] = fixture.currentMinute.split(':');
      return minute;
    }
  }, [isLiveMatch, fixture.currentMinute]);

  if (isMobile && isExpandable) {
    return (
      <>
        <div
          className={cn('fixture-match', className, {
            'fixture-match--expanded': isExpanded,
          })}
          onClick={toggleFixtureDetails}
        >
          <div
            className={cn('fixture-match__show-more-btn-container', {
              'fixture-match__show-more-btn-container--expandable': false,
            })}
          >
            <div className={'fixture-match__home-team-wrapper'}>
              <div className={'fixture-match__team-link'}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
                  {getClubName(fixture.homeTeam as Club)}
                </span>
                <Image
                  src={fixture.homeTeam.logo ?? 'default-team-badge.webp'}
                  alt={fixture.homeTeam.fullName}
                  width={30}
                  height={30}
                  className={'fixture-match__team-logo'}
                  style={!fixture.homeTeam.logo ? { width: 30, height: 30 } : undefined}
                  variant={ImageVariant.SquareLogoSmall}
                />
              </div>
              {isExpanded &&
                playerStats &&
                playerStats.homeTeamGoals?.map(({ player, goals }) => {
                  return (
                    <p key={`${player}-goals`} className={'fixture-match__home-player-stats'}>
                      {player}
                      {goals.length > 0 && <div>({goals.map((goal) => `${goal}'`).join(', ')})</div>}
                    </p>
                  );
                })}
              {isExpanded &&
                playerStats?.homeTeamRedCards?.map(({ player, redCard }) => {
                  return (
                    <p key={`${player}-goals`} className={'fixture-match__home-player-stats'}>
                      {player}
                      <div className={'match-overview__red-card'} />({redCard}')
                    </p>
                  );
                })}
            </div>
          </div>
          <div className={'fixture-match__score-link'}>
            {hasScores && (
              <div
                className={cn('fixture-match__score', {
                  'fixture-match__score--live': isLiveMatch,
                })}
              >
                <div>
                  <span>{fixture.score.current.home}</span>
                  <span> - </span>
                  <span>{fixture.score.current.away}</span>
                </div>
                {isLiveMatch && <div>{matchCurrentMinute}'</div>}
                {fixture.score.penalties && (
                  <span className="fixture-match__score__ht fixture-match__score__status">
                    {fixture.score.penalties.home} P {fixture.score.penalties.away}
                  </span>
                )}
                {fixture.status === MatchStatus.HalfTime && (
                  <span className="fixture-match__score__ht fixture-match__score__status">HT</span>
                )}
                {fixture.status === MatchStatus.FirstHalfOfExtraTime && (
                  <span className="fixture-match__score__fh_et fixture-match__score__status">ET</span>
                )}
                {fixture.status === MatchStatus.HalfTimeInExtraTime && (
                  <span className="fixture-match__score__ht_et fixture-match__score__status">HT</span>
                )}
                {fixture.status === MatchStatus.SecondHalfOfExtraTime && (
                  <span className="fixture-match__score__sh_et fixture-match__score__status">ET</span>
                )}
                {fixture.status === MatchStatus.FullTimeAfterExtraTime && (
                  <span className="fixture-match__score__ft_et fixture-match__score__status">AET</span>
                )}
                {fixture.status === MatchStatus.PenaltyShootOut && (
                  <span className="fixture-match__score__ps fixture-match__score__status">Pens</span>
                )}
              </div>
            )}
            {fixture.status === MatchStatus.Abandoned && <div className="fixture-match__time">A - A</div>}
            {fixture.status === MatchStatus.Postponed && <div className="fixture-match__time">P - P</div>}
            {fixture.status === MatchStatus.NotKickedOff && <div className="fixture-match__time">{formattedTime}</div>}
          </div>
          <div className={'fixture-match__away-team-wrapper'}>
            <div className="fixture-match__team-attendance-wrapper">
              <div className={'fixture-match__team-link'}>
                <Image
                  src={fixture.awayTeam.logo ?? 'default-team-badge.webp'}
                  alt={fixture.awayTeam.fullName}
                  width={30}
                  height={30}
                  className={'fixture-match__team-logo'}
                  style={!fixture.awayTeam.logo ? { width: 30, height: 30 } : undefined}
                  variant={ImageVariant.SquareLogoSmall}
                />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
                  {getClubName(fixture.awayTeam as Club)}
                </span>
              </div>
              {isExpandable ? (
                <div className={'fixture-match__show-more-btn'} style={{ width: '60px' }}>
                  {showAttendance && fixture.attendance && fixture.attendance > 0 ? (
                    <p className={'fixture-match__attendance'}>
                      Att: <span>{fixture.attendance}</span>
                    </p>
                  ) : null}
                </div>
              ) : (
                <>
                  {showAttendance && fixture.attendance && fixture.attendance > 0 ? (
                    <p className={'fixture-match__attendance'}>
                      Att: <span>{fixture.attendance}</span>
                    </p>
                  ) : null}
                </>
              )}
            </div>
            {isExpanded &&
              playerStats &&
              playerStats.awayTeamGoals?.map(({ player, goals }) => {
                return (
                  <p key={`${player}-goals`} className={'fixture-match__away-player-stats'}>
                    {player}
                    {goals.length > 0 && <div>({goals.map((goal) => `${goal}'`).join(', ')})</div>}
                  </p>
                );
              })}
            {isExpanded &&
              playerStats?.awayTeamRedCards?.map(({ player, redCard }) => {
                return (
                  <p key={`${player}-goals`} className={'fixture-match__away-player-stats'}>
                    {player}
                    <div className={'match-overview__red-card'} />({redCard}')
                  </p>
                );
              })}
          </div>
        </div>
        {fixture.info && (
          <Link className={'fixture-match__team-link'} to={`/match/${fixture.id}`}>
            <div className="fixture-match__info" dangerouslySetInnerHTML={{ __html: fixture.info }} />
          </Link>
        )}
        {isExpanded && (
          <LinkButton
            url={`/match/${fixture.id}`}
            variant={LinkVariant.Outlined}
            size={LinkSize.sm}
            label="Go to game"
            openInNewTab={false}
            style={{
              height: '4rem',
              display: 'block',
              textAlign: 'center',
              marginLeft: 'auto',
              marginRight: 'auto',
              marginTop: 'var(--spacing-3)',
            }}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div
        className={cn('fixture-match', className, {
          'fixture-match--expanded': isExpanded,
        })}
      >
        <div
          className={cn('fixture-match__show-more-btn-container', {
            'fixture-match__show-more-btn-container--expandable': isExpandable && !isMobile,
          })}
        >
          {isExpandable && !isMobile && (
            <button className={'fixture-match__show-more-btn'} onClick={toggleFixtureDetails}>
              <Image
                variant={ImageVariant.SquareLogoSmall}
                src={'web/chevron-right.svg'}
                alt={'show match details'}
                width={25}
                height={25}
                className={'fixture-match__show-more-icon'}
              />
            </button>
          )}
          <div className={'fixture-match__home-team-wrapper'}>
            <Link className={'fixture-match__team-link'} to={`/match/${fixture.id}`}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
                {getClubName(fixture.homeTeam as Club)}
              </span>
              <Image
                src={fixture.homeTeam.logo ?? 'default-team-badge.webp'}
                alt={fixture.homeTeam.fullName}
                width={30}
                height={30}
                className={'fixture-match__team-logo'}
                style={!fixture.homeTeam.logo ? { width: 30, height: 30 } : undefined}
                variant={ImageVariant.SquareLogoSmall}
              />
            </Link>
            {isExpanded &&
              playerStats &&
              playerStats.homeTeamGoals?.map(({ player, goals }) => {
                return (
                  <p key={`${player}-goals`} className={'fixture-match__home-player-stats'}>
                    {player}
                    {goals.length > 0 && <div>({goals.map((goal) => `${goal}'`).join(', ')})</div>}
                  </p>
                );
              })}
            {isExpanded &&
              playerStats?.homeTeamRedCards?.map(({ player, redCard }) => {
                return (
                  <p key={`${player}-goals`} className={'fixture-match__home-player-stats'}>
                    {player}
                    <div className={'match-overview__red-card'} />({redCard}')
                  </p>
                );
              })}
          </div>
        </div>
        <Link className={'fixture-match__score-link'} to={`/match/${fixture.id}`}>
          {hasScores && (
            <div
              className={cn('fixture-match__score', {
                'fixture-match__score--live': isLiveMatch,
              })}
            >
              <div>
                <span>{fixture.score.current.home}</span>
                <span> - </span>
                <span>{fixture.score.current.away}</span>
              </div>
              {isLiveMatch && <div>{matchCurrentMinute}'</div>}
              {fixture.score.penalties && (
                <span className="fixture-match__score__ht fixture-match__score__status">
                  {fixture.score.penalties.home} P {fixture.score.penalties.away}
                </span>
              )}
              {fixture.status === MatchStatus.HalfTime && (
                <span className="fixture-match__score__ht fixture-match__score__status">HT</span>
              )}
              {fixture.status === MatchStatus.FirstHalfOfExtraTime && (
                <span className="fixture-match__score__fh_et fixture-match__score__status">ET</span>
              )}
              {fixture.status === MatchStatus.HalfTimeInExtraTime && (
                <span className="fixture-match__score__ht_et fixture-match__score__status">HT</span>
              )}
              {fixture.status === MatchStatus.SecondHalfOfExtraTime && (
                <span className="fixture-match__score__sh_et fixture-match__score__status">ET</span>
              )}
              {fixture.status === MatchStatus.FullTimeAfterExtraTime && (
                <span className="fixture-match__score__ft_et fixture-match__score__status">AET</span>
              )}
              {fixture.status === MatchStatus.PenaltyShootOut && (
                <span className="fixture-match__score__ps fixture-match__score__status">Pens</span>
              )}
            </div>
          )}
          {fixture.status === MatchStatus.Abandoned && <div className="fixture-match__time">A - A</div>}
          {fixture.status === MatchStatus.Postponed && <div className="fixture-match__time">P - P</div>}
          {fixture.status === MatchStatus.NotKickedOff && <div className="fixture-match__time">{formattedTime}</div>}
        </Link>
        <div className={'fixture-match__away-team-wrapper'}>
          <div className="fixture-match__team-attendance-wrapper">
            <Link className={'fixture-match__team-link'} to={`/match/${fixture.id}`}>
              <Image
                src={fixture.awayTeam.logo ?? 'default-team-badge.webp'}
                alt={fixture.awayTeam.fullName}
                width={30}
                height={30}
                className={'fixture-match__team-logo'}
                style={!fixture.awayTeam.logo ? { width: 30, height: 30 } : undefined}
                variant={ImageVariant.SquareLogoSmall}
              />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
                {getClubName(fixture.awayTeam as Club)}
              </span>
            </Link>
            {isExpandable ? (
              <button
                className={'fixture-match__show-more-btn'}
                style={{ width: '60px' }}
                onClick={toggleFixtureDetails}
              >
                {showAttendance && fixture.attendance && fixture.attendance > 0 ? (
                  <p className={'fixture-match__attendance'}>
                    Att: <span>{fixture.attendance}</span>
                  </p>
                ) : null}
              </button>
            ) : (
              <>
                {showAttendance && fixture.attendance && fixture.attendance > 0 ? (
                  <p className={'fixture-match__attendance'}>
                    Att: <span>{fixture.attendance}</span>
                  </p>
                ) : null}
              </>
            )}
          </div>
          {isExpanded &&
            playerStats &&
            playerStats.awayTeamGoals?.map(({ player, goals }) => {
              return (
                <p key={`${player}-goals`} className={'fixture-match__away-player-stats'}>
                  {player}
                  {goals.length > 0 && <div>({goals.map((goal) => `${goal}'`).join(', ')})</div>}
                </p>
              );
            })}
          {isExpanded &&
            playerStats?.awayTeamRedCards?.map(({ player, redCard }) => {
              return (
                <p key={`${player}-goals`} className={'fixture-match__away-player-stats'}>
                  {player}
                  <div className={'match-overview__red-card'} />({redCard}')
                </p>
              );
            })}
        </div>
      </div>
      {fixture.info && (
        <Link className={'fixture-match__team-link'} to={`/match/${fixture.id}`}>
          <div className="fixture-match__info" dangerouslySetInnerHTML={{ __html: fixture.info }} />
        </Link>
      )}
    </>
  );
};
